import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../common/context/AuthContext';
import Toast from '../../common/components/Toast';
import { API_URL } from '../../api/config';

export default function OutletSelection() {
  const location = useLocation();
  const { outlets = [], email: passedEmail = '' } = location.state || {};
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [password, setPassword] = useState('');
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [attemptCount, setAttemptCount] = useState(0);
  const { userData, signIn: authSignIn } = useAuth();
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const closeToast = () => {
    setToast({ ...toast, show: false });
  };

  const currentEmail = userData?.email || passedEmail;

  useEffect(() => {
    if (outlets.length === 0) {
      showToast('No outlets available. Please try again.', 'error');
      setTimeout(() => navigate('/signin', { replace: true }), 1500);
      return;
    }
    
    if (outlets.length === 1) {
      setSelectedOutlet(outlets[0]);
      checkPreviousLogin(outlets[0]);
    }
  }, [outlets, navigate]);

  const checkPreviousLogin = async (outlet) => {
    try {
      const savedAccounts = localStorage.getItem('savedAccounts');
      if (savedAccounts) {
        const accounts = JSON.parse(savedAccounts);
        const existingAccount = accounts.find(
          account => account.restaurantId === outlet.restaurantId && account.email === currentEmail
        );
        if (existingAccount) {
          handleAutoLogin(outlet);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error checking previous login:', error);
      return false;
    }
  };

  const handleAutoLogin = async (outlet) => {
    try {
      setLoading(true);
      
      const response = await axios.post(`${API_URL}/auth/auto-login`, {
        email: currentEmail,
        restaurantId: outlet.restaurantId
      });

      if (response.data.success) {
        const { token, user: apiUser } = response.data;
        
        const account = {
          email: currentEmail,
          restaurantId: outlet.restaurantId,
          restaurantName: outlet.restaurantName,
          outletName: outlet.outletName
        };
        
        let savedAccounts = JSON.parse(localStorage.getItem('savedAccounts') || '[]');
        if (!Array.isArray(savedAccounts)) savedAccounts = [];
        if (!savedAccounts.some(a => a.email === currentEmail && a.restaurantId === outlet.restaurantId)) {
          savedAccounts.push(account);
          localStorage.setItem('savedAccounts', JSON.stringify(savedAccounts));
        }

        await authSignIn({ token, user: apiUser });
        showToast('Signed in successfully');
        setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
      }
    } catch (error) {
      console.error('Auto-login error:', error);
      setShowPasswordInput(true);
    } finally {
      setLoading(false);
    }
  };

  const handleOutletSelect = async () => {
    if (!selectedOutlet) {
      showToast('Please select an outlet', 'error');
      return;
    }
    const isPreviouslyLoggedIn = await checkPreviousLogin(selectedOutlet);
    if (!isPreviouslyLoggedIn) {
      setShowPasswordInput(true);
    }
    setErrorMessage('');
  };

  const handlePasswordSubmit = async () => {
    if (!password) {
      setErrorMessage('Please enter your password');
      return;
    }
    try {
      setLoading(true);
      setErrorMessage('');
      
      const response = await axios.post(`${API_URL}/auth/select-outlet`, {
        email: currentEmail,
        restaurantId: selectedOutlet.restaurantId,
        password
      });

      if (response.data.success) {
        const { token, user: apiUser } = response.data;
        
        const account = {
          email: currentEmail,
          restaurantId: selectedOutlet.restaurantId,
          restaurantName: selectedOutlet.restaurantName,
          outletName: selectedOutlet.outletName
        };
        
        let savedAccounts = JSON.parse(localStorage.getItem('savedAccounts') || '[]');
        if (!Array.isArray(savedAccounts)) savedAccounts = [];
        if (!savedAccounts.some(a => a.email === currentEmail && a.restaurantId === selectedOutlet.restaurantId)) {
          savedAccounts.push(account);
          localStorage.setItem('savedAccounts', JSON.stringify(savedAccounts));
        }

        await authSignIn({ token, user: apiUser });
        showToast('Signed in successfully');
        setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
      }
    } catch (error) {
      console.log('Outlet selection error:', error);
      setAttemptCount(prev => prev + 1);
      if (error.response?.status === 429) {
        const message = 'Too many attempts. Please try again later.';
        setErrorMessage(message);
        showToast(message, 'error');
        setTimeout(() => navigate(-1), 1500);
      } else if (error.response?.status === 401) {
        setErrorMessage('Incorrect password. Please try again.');
        setPassword('');
      } else {
        const message = error.response?.data?.message || 'An error occurred while selecting outlet';
        setErrorMessage(message);
        showToast(message, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      {toast.show && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}
      
      <div style={{
        backgroundColor: 'white',
        borderRadius: '24px',
        padding: '60px 50px',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)'
      }}>
        {!showPasswordInput ? (
          <>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <h1 style={{
                fontSize: '32px',
                fontWeight: '700',
                color: '#111827',
                margin: '0 0 10px 0'
              }}>
                Select Outlet
              </h1>
              <p style={{
                fontSize: '16px',
                color: '#6b7280',
                margin: 0
              }}>
                Choose the outlet you want to sign in to
              </p>
            </div>

            {/* Outlets List */}
            <div style={{ marginBottom: '24px' }}>
              {outlets.map((item) => (
                <div
                  key={item.restaurantId}
                  onClick={() => setSelectedOutlet(item)}
                  style={{
                    padding: '16px',
                    marginBottom: '12px',
                    borderRadius: '12px',
                    border: selectedOutlet?.restaurantId === item.restaurantId 
                      ? '2px solid #2563eb' 
                      : '1px solid #e5e7eb',
                    cursor: 'pointer',
                    backgroundColor: selectedOutlet?.restaurantId === item.restaurantId 
                      ? '#eff6ff' 
                      : '#ffffff',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedOutlet?.restaurantId !== item.restaurantId) {
                      e.currentTarget.style.borderColor = '#d1d5db';
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedOutlet?.restaurantId !== item.restaurantId) {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.backgroundColor = '#ffffff';
                    }
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{
                        fontWeight: '600',
                        color: '#111827',
                        margin: '0 0 4px 0',
                        fontSize: '15px'
                      }}>
                        {item.restaurantName}
                      </h3>
                      <p style={{
                        color: '#6b7280',
                        fontSize: '14px',
                        margin: '4px 0'
                      }}>
                        {item.outletName}
                      </p>
                      <p style={{
                        color: '#9ca3af',
                        fontSize: '12px',
                        margin: '4px 0 0 0'
                      }}>
                        ID: {item.restaurantId}
                      </p>
                    </div>
                    {selectedOutlet?.restaurantId === item.restaurantId && (
                      <div style={{
                        color: '#2563eb',
                        fontWeight: 'bold',
                        fontSize: '20px',
                        marginLeft: '12px'
                      }}>
                        ✓
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Continue Button */}
            <button
              onClick={handleOutletSelect}
              disabled={!selectedOutlet || loading}
              style={{
                width: '100%',
                padding: '12px 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: !selectedOutlet || loading ? 'not-allowed' : 'pointer',
                opacity: !selectedOutlet || loading ? 0.7 : 1,
                transition: 'background-color 0.3s ease'
              }}
              onMouseEnter={(e) => !selectedOutlet || loading ? null : (e.target.style.backgroundColor = '#1d4ed8')}
              onMouseLeave={(e) => !selectedOutlet || loading ? null : (e.target.style.backgroundColor = '#2563eb')}
            >
              {loading ? 'Loading...' : 'Continue'}
            </button>
          </>
        ) : (
          <>
            {/* Password Header */}
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <h1 style={{
                fontSize: '32px',
                fontWeight: '700',
                color: '#111827',
                margin: '0 0 10px 0'
              }}>
                Verify Password
              </h1>
              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                margin: '0'
              }}>
                {selectedOutlet?.restaurantName} • {selectedOutlet?.outletName}
              </p>
            </div>

            {/* Password Input */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#111827',
                marginBottom: '8px'
              }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <Lock style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#2563eb',
                  pointerEvents: 'none'
                }} size={20} />
                <input
                  placeholder="••••••••"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errorMessage) setErrorMessage('');
                  }}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '12px 16px 12px 40px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.3s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
            </div>

            {/* Error Message */}
            {errorMessage && (
              <p style={{
                color: '#dc2626',
                fontSize: '14px',
                marginBottom: '16px',
                padding: '10px 12px',
                backgroundColor: '#fee2e2',
                borderRadius: '8px',
                margin: '0 0 16px 0'
              }}>
                {errorMessage}
              </p>
            )}

            {/* Sign In Button */}
            <button
              onClick={handlePasswordSubmit}
              disabled={!password || loading || attemptCount >= 5}
              style={{
                width: '100%',
                padding: '12px 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: !password || loading || attemptCount >= 5 ? 'not-allowed' : 'pointer',
                opacity: !password || loading || attemptCount >= 5 ? 0.7 : 1,
                transition: 'background-color 0.3s ease',
                marginBottom: '12px'
              }}
              onMouseEnter={(e) => !password || loading || attemptCount >= 5 ? null : (e.target.style.backgroundColor = '#1d4ed8')}
              onMouseLeave={(e) => !password || loading || attemptCount >= 5 ? null : (e.target.style.backgroundColor = '#2563eb')}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>

            {/* Back Button */}
            <button
              onClick={() => {
                setShowPasswordInput(false);
                setPassword('');
                setErrorMessage('');
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                backgroundColor: 'transparent',
                color: '#2563eb',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f9fafb';
                e.target.style.borderColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.borderColor = '#e5e7eb';
              }}
            >
              <ChevronLeft size={16} />
              Back to outlets
            </button>
          </>
        )}
      </div>

      {/* Footer Status */}
      <div style={{
        marginTop: '40px',
        display: 'flex',
        gap: '20px',
        fontSize: '12px',
        color: '#6b7280'
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ width: '8px', height: '8px', backgroundColor: '#10b981', borderRadius: '50%' }}></span>
          Secure AES-256
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ width: '8px', height: '8px', backgroundColor: '#10b981', borderRadius: '50%' }}></span>
          System Online
        </span>
      </div>

      {/* Copyright */}
      <div style={{
        marginTop: '40px',
        textAlign: 'center',
        fontSize: '12px',
        color: '#6b7280'
      }}>
        <p style={{ margin: '0' }}>© 2024 TOBO INC. • ALL RIGHTS RESERVED</p>
      </div>
    </div>
  );
}